import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Tooltip
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

const IFrameTimeout = 10000; // 10 seconds in milliseconds
const ButtonSizes = {
  xs: { height: '22px', width: '70px', fontSize: '0.65rem' },
  sm: { height: '24px', width: '85px', fontSize: '0.7rem' }
};

const ElasticSearch = ({ name, index_name, analytics_panel_id }) => {
  const [opengrafana, setOpengrafana] = useState(false);
  const [iframeState, setIframeState] = useState('loading'); // 'loading', 'loaded', 'error'
  const timeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClickOpenGrafana = useCallback(() => {
    setOpengrafana(true);
    setIframeState('loading');
    
    // Set a timeout to detect unresponsive iframes
    timeoutRef.current = setTimeout(() => {
      setIframeState(prev => prev === 'loading' ? 'error' : prev);
    }, IFrameTimeout);
  }, []);

  const handleCloseGrafana = useCallback(() => {
    setOpengrafana(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleIframeLoad = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIframeState('loaded');
  }, []);

  const handleIframeError = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIframeState('error');
  }, []);

  // Memoize URL construction to avoid unnecessary recalculations
  const grafanapanelurl = useMemo(() => {
    const grafanadomain = `${process.env.REACT_APP_GRAFANA_URL}/d/`;
    const org = "?orgId=1";
    const kiosk = "&kiosk";
    return `${grafanadomain}${analytics_panel_id}${org}${kiosk}`;
  }, [analytics_panel_id]);
  
  // Memoize content rendering to avoid unnecessary re-renders
  const renderContent = useMemo(() => {
    const getIframeHeight = () => {
      // Responsive iframe height based on viewport
      return window.innerHeight < 600 ? '400px' : '500px';
    };

    switch (iframeState) {
      case 'loading':
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height={{ xs: '400px', sm: '500px' }}>
            <Typography>Loading report...</Typography>
          </Box>
        );
      case 'error':
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height={{ xs: '400px', sm: '500px' }}>
            <Typography color="error">
              No information is available right now. Please try again later.
            </Typography>
          </Box>
        );
      case 'loaded':
        return (
          <iframe 
            src={grafanapanelurl}
            width="100%" 
            height={getIframeHeight()}
            frameBorder="0"
            title="Grafana Dashboard"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        );
      default:
        return null;
    }
  }, [iframeState, grafanapanelurl, handleIframeLoad, handleIframeError]);

  const serverName = name && name.trim() !== '' ? name : 'N/A';

  return (
    <>
      <Tooltip title="Usage Report" arrow>
        <Button
          onClick={handleClickOpenGrafana}
          variant="contained"
          color="primary"
          aria-label="View usage report"
          size="small"
          sx={{ 
            mx: 1,
            minWidth: { xs: ButtonSizes.xs.width, sm: ButtonSizes.sm.width },
            height: { xs: ButtonSizes.xs.height, sm: ButtonSizes.sm.height },
            fontSize: { xs: ButtonSizes.xs.fontSize, sm: ButtonSizes.sm.fontSize },
            padding: { xs: '3px 5px', sm: '4px 7px' },
            borderRadius: '50px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          }}
        >
          Report
        </Button>
      </Tooltip>
      <Dialog
        open={opengrafana}
        onClose={handleCloseGrafana}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          Server Name: {serverName}
        </DialogTitle>
        <DialogContent>
          {renderContent}
        </DialogContent>
        <DialogActions>
          <Tooltip title="Close" arrow>
            <Button onClick={handleCloseGrafana} color="primary" autoFocus>
              Close
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>        
    </>
  );
}

export default ElasticSearch;